// powerLineIdentity.js
//
// Which circuit is this tower on, and at what voltage (#209).
//
// `power_lines.geojson` is already fetched to draw the corridor; its attributes
// were thrown away. They are the three things a maintenance engineer asks first,
// because they decide who has to be called before anyone climbs. "Node 2" is not
// how the asset is named in any document the operator holds.
//
// WHAT THIS IS NOT
//
// It is not an ordering, and nothing here may become one. #136 settled that on
// 2026-09-02 and the rule stands: segment_order is hand-seeded and NEVER
// inferred from this file, because 1,254 OSM LineStrings spanning Bosnia to
// Albania carry zero tower points, and snapping five nodes to them produces a
// confident, wrong line. This module answers a strictly weaker question — "which
// mapped line passes nearest this point, and what does OSM say about it" — and
// the caller is expected to say "nearest", not "belongs", wherever that
// distinction is not already settled by the node's own off_corridor_reason.
//
// WHAT THE DATA ACTUALLY HOLDS (measured 2026-10-28 over all 1,254 features)
//
//   power 1254 · voltage 755 · cables 341 · line 195 · circuits 123
//   frequency 109 · name 44 · operator 27 · wires 19 · ref 1
//
// So coverage is thin and uneven, and absence is the normal case rather than the
// exception. The three pilot nodes match a feature carrying voltage and
// frequency and nothing else: no name, no circuits, no operator. Every field is
// therefore optional, independently, and a field that is missing is rendered as
// nothing at all. Never as a blank row, and never inferred from a neighbour —
// a guessed voltage next to a tower is worse than no voltage.

// Beyond this, no line is reported. The three corridor nodes sit 1-5 m from
// their line, so the threshold is not fighting for them; it exists for the other
// end, where the nearest mapped line is 663 m away (node-1787075939823) and is
// not this node's circuit in any sense. 250 m is wide enough to absorb OSM
// digitising error and the fact that a line is drawn through tower centres,
// and far short of that 663 m.
export const NEAREST_LINE_MAX_M = 250;

const EARTH_RADIUS_M = 6371000;
const toRad = (deg) => (deg * Math.PI) / 180;

// Point-to-segment distance on a plane tangent at the node. Over the few hundred
// metres this function is ever asked about, the error against a proper geodesic
// is well under a metre — far below the uncertainty in where OSM drew the line —
// and it costs one cosine for the whole query instead of a haversine per
// segment, which matters at 28,571 segments.
function distanceToSegmentM(point, a, b) {
  const scale = EARTH_RADIUS_M * Math.cos(toRad(point[0]));
  const px = toRad(point[1]) * scale;
  const py = toRad(point[0]) * EARTH_RADIUS_M;
  const ax = toRad(a[1]) * scale;
  const ay = toRad(a[0]) * EARTH_RADIUS_M;
  const bx = toRad(b[1]) * scale;
  const by = toRad(b[0]) * EARTH_RADIUS_M;

  const vx = bx - ax;
  const vy = by - ay;
  const wx = px - ax;
  const wy = py - ay;
  const lenSq = vx * vx + vy * vy;
  // A zero-length segment (a repeated coordinate, which OSM data does contain)
  // collapses to the distance to its single endpoint rather than dividing by 0.
  const t = lenSq > 0 ? Math.max(0, Math.min(1, (wx * vx + wy * vy) / lenSq)) : 0;

  return Math.hypot(wx - t * vx, wy - t * vy);
}

// Direction of a segment on the same tangent plane, 0 to 180° from north, or
// null for a zero-length segment.
function segmentBearingDeg(a, b) {
  const dx = (toRad(b[1]) - toRad(a[1])) * Math.cos(toRad((a[0] + b[0]) / 2));
  const dy = toRad(b[0]) - toRad(a[0]);
  if (dx === 0 && dy === 0) return null;
  const deg = (Math.atan2(dx, dy) * 180) / Math.PI;
  return ((deg % 180) + 180) % 180;
}

// Substation internals, not spans between towers. `line=bay` (112 features) and
// `line=busbar` (77) are inside a fenced yard; a tower is not on one, so matching
// a node to one would name the wrong asset with full confidence. Matched with
// startsWith because five of those values are corrupt in the source file, with
// unrelated prose concatenated onto the tag ("busbarprepared under the Western
// Balkans Investment Framework by IPF1...").
function isSubstationInternal(properties) {
  const line = properties?.line;
  if (typeof line !== 'string') return false;
  return line.startsWith('bay') || line.startsWith('busbar');
}

/**
 * The mapped power line passing nearest `position`, or null.
 *
 * @param {[number, number]} position  node position as [lat, lng]
 * @param {object} geojson             the already-loaded power_lines.geojson
 * @param {number} maxDistanceM        beyond this, no line is reported
 * @returns {{ distanceM: number, properties: object, bearingDeg: number|null } | null}
 *
 * `bearingDeg` is the direction of the nearest segment, 0 to 180° clockwise
 * from north (a line has no direction of travel). The line rating panel (#247)
 * offers it as a prefill for the span azimuth, which the operator confirms; it
 * is only as good as where OSM drew the line, and a tower where the line turns
 * has two spans.
 */
export function nearestPowerLine(position, geojson, maxDistanceM = NEAREST_LINE_MAX_M) {
  if (!Array.isArray(position) || position.length !== 2) return null;
  if (!position.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;

  const features = geojson?.features;
  if (!Array.isArray(features)) return null;

  let best = null;

  for (const feature of features) {
    const properties = feature?.properties ?? {};
    // An abandoned line still stands on the map and would still be drawn here as
    // this tower's circuit. It is not energised and naming it would mislead.
    if (properties.abandoned) continue;
    if (isSubstationInternal(properties)) continue;

    const geometry = feature?.geometry;
    if (geometry?.type !== 'LineString') continue;
    const coords = geometry.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) continue;

    for (let i = 0; i < coords.length - 1; i += 1) {
      const from = coords[i];
      const to = coords[i + 1];
      if (!Array.isArray(from) || !Array.isArray(to)) continue;
      // GeoJSON is [lng, lat]; everything else in this app is [lat, lng].
      const d = distanceToSegmentM(position, [from[1], from[0]], [to[1], to[0]]);
      // Number.isFinite, not a check on the coordinates, because NaN loses no
      // comparison: `NaN < best.distanceM` is false, so a NaN that reaches this
      // line as the first candidate becomes `best` and nothing can displace it.
      // `NaN > maxDistanceM` is false too, so the guard below would then return
      // it rather than reject it, and every node on the map would be labelled
      // with that one feature's voltage under "Nearest mapped line, null away."
      // A truncated coordinate pair is enough to cause it: toRad(undefined) is
      // NaN, while toRad(null) is 0, so the Array.isArray check above cannot
      // see the difference.
      if (Number.isFinite(d) && (best === null || d < best.distanceM))
        best = { distanceM: d, properties, bearingDeg: segmentBearingDeg([from[1], from[0]], [to[1], to[0]]) };
    }
  }

  if (best === null || best.distanceM > maxDistanceM) return null;
  return best;
}

// Transmission voltages are tagged in volts. A value under 1000 is ambiguous —
// the file holds one feature tagged `voltage=110`, which is either 110 V or
// someone who meant 110 kV — and resolving it either way is a guess about what
// is standing next to an engineer. It is dropped instead.
const MIN_PLAUSIBLE_VOLTS = 1000;

function parseVoltageKv(raw) {
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  // OSM separates the voltages of a shared tower with a semicolon. None of the
  // 755 tagged features does today, but the tag permits it and silently showing
  // only the first would understate what the tower carries.
  const parts = String(raw)
    .split(';')
    .map((p) => Number(p.trim()))
    .filter((v) => Number.isFinite(v) && v >= MIN_PLAUSIBLE_VOLTS);
  if (parts.length === 0) return null;
  return parts.map((v) => v / 1000);
}

function parseCount(raw) {
  if (raw === undefined || raw === null) return null;
  const n = Number(String(raw).trim());
  return Number.isInteger(n) && n > 0 ? n : null;
}

function parseText(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The displayable facts about a matched line. Every field is independently
 * nullable, and null means "the source does not say" — never a default.
 *
 * @param {object} properties  the matched feature's OSM-derived properties
 * @returns {{ voltagesKv: number[]|null, name: string|null, ref: string|null,
 *             circuits: number|null, operator: string|null, cables: number|null,
 *             frequencyHz: number|null }}
 */
export function lineIdentity(properties) {
  const p = properties ?? {};
  return {
    voltagesKv: parseVoltageKv(p.voltage),
    // `name:en` is a fallback rather than a preference: the operator's own
    // documents use the local name, and only one feature carries an English one.
    name: parseText(p.name) ?? parseText(p['name:en']),
    ref: parseText(p.ref),
    circuits: parseCount(p.circuits),
    // operator:short is what fits a panel ("CGES"), but it is only ever present
    // alongside the full name, so the full name is what gets shown.
    operator: parseText(p.operator) ?? parseText(p['operator:short']),
    cables: parseCount(p.cables),
    frequencyHz: parseCount(p.frequency),
  };
}

/** True when a matched line has nothing worth drawing a row for. */
export function isIdentityEmpty(identity) {
  if (!identity) return true;
  return Object.values(identity).every((v) => v === null);
}

/** "110 kV", "400 / 110 kV", or null. Integers stay integers. */
export function formatVoltage(voltagesKv) {
  if (!Array.isArray(voltagesKv) || voltagesKv.length === 0) return null;
  const parts = voltagesKv.map((v) => (Number.isInteger(v) ? String(v) : String(Number(v.toFixed(1)))));
  return `${parts.join(' / ')} kV`;
}

/**
 * Distance rounded the way it is read: metres under a kilometre, else km.
 *
 * Sub-metre matches are real — one node sits 0.4 m from a mapped line — and
 * rounding those to "0 m away" reads as a failed measurement rather than a very
 * close one.
 */
export function formatDistance(distanceM) {
  if (!Number.isFinite(distanceM)) return null;
  if (distanceM < 1) return '<1 m';
  if (distanceM < 1000) return `${Math.round(distanceM)} m`;
  return `${(distanceM / 1000).toFixed(1)} km`;
}
